import { formatPrice, toNumber } from "./price";

/**
 * Phase-2 OFFER LADDER — the N-tier decomposition that powers the complex
 * signature legals (INTERNET / BYOD / SATELLITE plan cards). It is the ONE place
 * the tiered "$30 off 1st mo.; $10 off mos 2-24 / Up to $88.98 …; $108.98 …" math
 * lives, ported + generalized from the 2-tier-hardcoded logic in
 * `plan-card-adapter.ts` (`evaluateReqPriceFormula` + `getMultiOfferDuration`) so
 * every plan-card surface resolves the SAME numbers from EPOCH — with zero code
 * change when offers change and no cap at two offers.
 *
 * Model (verified 2026-08 against the then-live CHOICE legal; re-verify against
 * current legal copy if this stops matching rendered card text — the algorithm is
 * durable, the promo it was checked against is not): `offerPrice` is the fully
 * discounted month-1 price; each EPOCH benefit `{discount, periodMo}` is a promo
 * active for months 1..periodMo. Sorting benefits by `periodMo` ascending, the
 * ladder has one segment per DISTINCT `periodMo` boundary. Within a segment:
 *   - active discount (what "$X off" shows) = Σ discounts NOT yet expired
 *     (`periodMo >= boundary`);
 *   - net price = `offerPrice + Σ expired discounts` (each expiring benefit adds
 *     its discount back);
 *   - `reqPrice` = `net + deviceFee`, RSN EXCLUDED — RSN is zip-specific and the
 *     client re-adds it on a zip change, exactly like plan cards today. A separate
 *     RSNFEE token exposes the baked default-zip value.
 */

/** One EPOCH promo benefit as the ladder needs it. */
export interface OfferBenefit {
  discount: number;
  periodMo: number;
  code?: string;
}

/** One resolved time-segment of the ladder (values display-ready / null = suppress). */
export interface LadderSegment {
  /** Discount active during this segment, e.g. 30. Rounded to whole dollars to
   *  match the shipped legal ("$30 off"). null when it cannot be computed. */
  amount: number | null;
  /** Duration phrase for this segment, e.g. "1st mo" / "2-24" (see getMultiOfferDuration). */
  durationText: string | null;
  /** All-in price for the segment EXCLUDING RSN (net + device fee), a 2-dp string
   *  ("74.99") for parity with the legacy adapter; the CMS supplies the `$`. */
  reqPrice: string | null;
}

/** 1 → "1st", 2 → "2nd", 3 → "3rd", 11 → "11th" … (ported from plan-cards utils). */
const getOrdinal = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Duration phrase for a segment spanning `startMonth..endMonth`. This is the SINGLE
 * source of truth: `src/components/video-lab/plan-cards/utils/dynamicPrices.ts` now
 * re-exports it rather than carrying a copy, so a token-authored legal and a legacy
 * one cannot drift apart. (It lives in `lib/` because the dependency only runs
 * src → lib, never the reverse.)
 *   getMultiOfferDuration(null, 24) → "24 mos" ; (null, 1) → "1st mo"
 *   getMultiOfferDuration(1, 1)     → "1st mo"  ; (2, 24)   → "2-24"
 */
export const getMultiOfferDuration = (startMonth: number | null, endMonth: number): string => {
  if (startMonth === null && endMonth > 1) {
    return `${endMonth} mos`;
  }
  if (startMonth === null && endMonth === 1) {
    return "1st mo";
  } else if (startMonth === endMonth) {
    return `${getOrdinal(startMonth)} mo`;
  } else if (endMonth - startMonth === 1) {
    return `${startMonth}&${endMonth}`;
  }
  return `${startMonth}-${endMonth}`;
};

/**
 * Build the N-segment offer ladder, or `null` when there is no base price or no
 * genuine promo benefit (→ every `OFFER<n>*` token self-suppresses and the author
 * falls back to OFFERPRICE + static copy). Pure + deterministic; no fetching.
 */
export function buildOfferLadder(
  offerPrice: string | number | null | undefined,
  deviceFee: string | number | null | undefined,
  benefits: OfferBenefit[] | null | undefined
): LadderSegment[] | null {
  const base = toNumber(offerPrice);
  if (base === null) {
    return null;
  }
  const device = toNumber(deviceFee) ?? 0;

  // Keep only real, in-window discounts, sorted by when they expire (shortest first).
  const valid = (benefits ?? [])
    .map(b => ({
      discount: Number(b?.discount) || 0,
      periodMo: Number(b?.periodMo) || 0
    }))
    .filter(b => b.discount > 0 && b.periodMo > 0)
    .sort((a, b) => a.periodMo - b.periodMo);
  if (valid.length === 0) {
    return null;
  }

  // Distinct expiry boundaries (benefits that share a periodMo collapse into one segment).
  const boundaries: number[] = [];
  for (const b of valid) {
    if (boundaries[boundaries.length - 1] !== b.periodMo) {
      boundaries.push(b.periodMo);
    }
  }
  const single = boundaries.length === 1;

  let prev = 0;
  const segments: LadderSegment[] = boundaries.map((boundary, i) => {
    const active = valid.filter(b => b.periodMo >= boundary).reduce((s, b) => s + b.discount, 0);
    const expired = valid.filter(b => b.periodMo < boundary).reduce((s, b) => s + b.discount, 0);
    const net = base + expired;
    // First segment starts month 1 (or null → "N mos"/"1st mo" when it's the only one).
    const startMonth = i === 0 ? (single ? null : 1) : prev + 1;
    const segment: LadderSegment = {
      amount: Math.round(active),
      durationText: getMultiOfferDuration(startMonth, boundary),
      reqPrice: formatPrice(net + device)
    };
    prev = boundary;
    return segment;
  });
  return segments;
}
